import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, Chip, IconButton, Tooltip, TextField,
  InputAdornment, CircularProgress, TablePagination,
  Grid, Card, CardContent, Accordion, AccordionSummary, AccordionDetails,
  useTheme, useMediaQuery, Stack, Divider, Collapse, Pagination, CardHeader,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HeightIcon from '@mui/icons-material/Height';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SportsIcon from '@mui/icons-material/Sports';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerIcon from '@mui/icons-material/Timer';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { usePositions } from '../utils/positions';
import PlayerTypeToggle from './PlayerTypeToggle';
import { matchesPlayerTypeValue } from '../utils/playerTypes';
import { currentUserCanUseProspectFilter } from '../utils/prospectUtils';

// Fonctions de conversion US/EU
function feetInchesToCm(feet, inches) {
  const f = parseFloat(feet) || 0;
  const i = parseFloat(inches) || 0;
  const totalInches = f * 12 + i;
  return totalInches * 2.54;
}

function inchesToCm(inches) {
  const i = parseFloat(inches) || 0;
  return i * 2.54;
}

function lbsToKg(lbs) {
  const l = parseFloat(lbs) || 0;
  return l * 0.45359237;
}

function cmToFeetInches(cm) {
  const c = parseFloat(cm) || 0;
  const totalInches = c / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return { feet, inches: inches.toFixed(2) };
}

function cmToInches(cm) {
  const c = parseFloat(cm) || 0;
  return (c / 2.54).toFixed(2);
}

function kgToLbs(kg) {
  const k = parseFloat(kg) || 0;
  return (k / 0.45359237).toFixed(2);
}

// Styled Components
const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '12px !important',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: theme.spacing(2, 0),
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '8px !important',
    marginBottom: theme.spacing(1),
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  minHeight: '80px',
  '&.Mui-expanded': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    minHeight: '80px',
  },
  '& .MuiAccordionSummary-content': {
    margin: '16px 0',
    alignItems: 'center',
  },
}));

const PlayerChip = styled(Chip)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.1rem',
  borderRadius: '20px',
  padding: '8px 12px',
  height: 'auto',
  '& .MuiChip-avatar': {
    width: 48,
    height: 48,
    marginLeft: '-12px',
    marginRight: '8px',
  },
  '& .MuiChip-label': {
    paddingLeft: 0,
    paddingRight: 4,
  }
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1.5),
    borderRadius: '6px',
    '& .MuiCardContent-root': {
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(2),
      },
    },
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),
}));

const MetricBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));


// Mobile Card Component
const MobileMedidaCard = ({ medida, open, onToggle, navigate, previousMedida, onEdit, onDelete, t, translateFromDB }) => {
  const theme = useTheme();
  const birthYear = medida.player_date_naissance ? new Date(medida.player_date_naissance).getFullYear() : null;
  const decimalAge = calculateAge(medida.player_date_naissance, medida.fecha);
  const shortNat = getShortNationality(medida.player_nationalite);

  return (
    <Card sx={{ mb: 2, boxShadow: theme.shadows[2] }}>
      <CardHeader
        avatar={
          <Avatar src={getPlayerAvatarUrl(medida.player_id)} alt={medida.player_nom} />
        }
        title={
          <Typography 
            onClick={() => navigate(`/players/${medida.player_id}`)}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' }}}
          >
            {medida.player_nom}
          </Typography>
        }
        subheader={new Date(medida.fecha).toLocaleDateString()}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={() => onEdit(medida)} sx={{ color: 'primary.main' }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(medida)} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => onToggle(medida.id)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        }
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('playerCardCreator.medidasForm.event')}:</strong> {medida.fuente}
            </Typography>
          </Box>

          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('allMedidas.position')}:</strong> {translateFromDB(medida.player_poste) || 'N/A'}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('allMedidas.age')}:</strong> {birthYear && decimalAge ? `${birthYear} (${decimalAge})` : 'N/A'}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{t('allMedidas.nationality')}:</strong> {shortNat || 'N/A'}
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" component="span">
              <strong>{t('allMedidas.team')}: </strong>
            </Typography>
            {/* Si on a des données dans competitions_detail, les afficher en priorité */}
            {medida.player_competitions_detail && medida.player_competitions_detail.length > 0 ? (
              <Box sx={{ display: 'inline-flex', flexDirection: 'column', ml: 1 }}>
                {/* Grouper par équipe */}
                {[...new Set(medida.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                  // Récupérer l'ID de l'équipe et les compétitions associées
                  const equipeData = medida.player_competitions_detail.find(c => c.equipe_nom === equipe);
                  const competitions = medida.player_competitions_detail
                    .filter(c => c.equipe_nom === equipe)
                    .filter((value, index, self) =>
                      index === self.findIndex((c) => c.competition_id === value.competition_id)
                    );
                  const mediaUrl = getMediaBaseUrl();
                  const timestamp = Date.now();

                  return (
                    <Box key={idx} sx={{ mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* Logo de l'équipe */}
                        <Box
                          component="img"
                          src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                          alt={equipe}
                          sx={{
                            width: 20,
                            height: 20,
                            objectFit: 'contain',
                            borderRadius: '2px'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {equipe}
                        </Typography>
                      </Box>
                      {/* Logos et noms des compétitions */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 3 }}>
                        {competitions.map((comp, compIdx) => (
                          <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <Box
                              component="img"
                              src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                              alt={comp.competition_nom}
                              sx={{
                                width: 16,
                                height: 16,
                                objectFit: 'contain',
                                borderRadius: '2px'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Typography variant="caption">{comp.competition_nom}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              // Sinon afficher l'ancien système
              <Typography variant="body2" component="span">
                {medida.player_team || 'N/A'}
                {(medida.player_league_1 || medida.player_league_2) && (
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {' '}({[medida.player_league_1, medida.player_league_2].filter(Boolean).join(' - ')})
                  </span>
                )}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          {renderMedidaContent(medida, previousMedida)}
        </CardContent>
      </Collapse>
    </Card>
  );
};

// Helper functions
const calculateAge = (dateNaissance, dateFiche) => {
  if (!dateNaissance) return '';
  const birth = new Date(dateNaissance);
  const today = new Date(); // Utiliser la date du jour
  
  if (isNaN(birth.getTime())) {
    return '';
  }
  
  const diffMs = today - birth;
  const ageInYears = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  
  return ageInYears.toFixed(1);
};

const getShortNationality = (nat) => {
  if (!nat) return '';
  const match = nat.match(/\((.*?)\)/);
  if (match) return match[1];
  return nat.slice(0, 3).toUpperCase();
};

const getPlayerAvatarUrl = (playerId) => {
  return `${getMediaBaseUrl()}/photos/${playerId}.png?${new Date().getTime()}`;
};

const calculateEvolution = (currentValue, previousValue, inverse = false) => {
  if (!currentValue || !previousValue) return null;
  const current = parseFloat(currentValue);
  const previous = parseFloat(previousValue);
  if (isNaN(current) || isNaN(previous)) return null;
  
  const diff = current - previous;
  const percentChange = ((diff / previous) * 100).toFixed(1);
  
  let trend = 'stable';
  if (Math.abs(diff) > 0.01) {
    trend = inverse ? (diff < 0 ? 'up' : 'down') : (diff > 0 ? 'up' : 'down');
  }
  
  return { diff: diff.toFixed(2), percentChange, trend };
};

const getTrendIcon = (trend) => {
  switch (trend) {
    case 'up':
      return <TrendingUpIcon sx={{ fontSize: 16, color: '#4CAF50' }} />;
    case 'down':
      return <TrendingDownIcon sx={{ fontSize: 16, color: '#F44336' }} />;
    default:
      return <RemoveIcon sx={{ fontSize: 16, color: '#9E9E9E' }} />;
  }
};

const formatValueWithEvolution = (value, evolution, unit) => {
  if (!value) return <Typography variant="body2" color="text.secondary">---</Typography>;
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}{unit && ` ${unit}`}
      </Typography>
      {evolution && (
        <Tooltip title={`${evolution.diff > 0 ? '+' : ''}${evolution.diff} (${evolution.percentChange}%)`}>
          <Box>{getTrendIcon(evolution.trend)}</Box>
        </Tooltip>
      )}
    </Box>
  );
};

// Configuration des métriques
const metrics = [
  { 
    key: 'altura', 
    label: 'Altura', 
    unit: 'cm', 
    category: 'MEDIDAS ANTROPOMÉTRICAS',
    icon: <HeightIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'altura_con_zapatos', 
    label: 'Altura c/Zapatos', 
    unit: 'cm', 
    category: 'MEDIDAS ANTROPOMÉTRICAS',
    icon: <HeightIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'envergadura', 
    label: 'Envergadura', 
    unit: 'cm', 
    category: 'MEDIDAS ANTROPOMÉTRICAS',
    icon: <SportsIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'alcance', 
    label: 'Alcance', 
    unit: 'cm', 
    category: 'MEDIDAS ANTROPOMÉTRICAS',
    icon: <SportsIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'salto_vertical_maximo', 
    label: 'Salto Vertical Máx.', 
    unit: 'cm', 
    category: 'PRUEBAS DE EXPLOSIVIDAD',
    icon: <SportsIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'salto_vertical_de_pie', 
    label: 'Salto Vertical de Pie', 
    unit: 'cm', 
    category: 'PRUEBAS DE EXPLOSIVIDAD',
    icon: <SportsIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'peso', 
    label: 'Peso', 
    unit: 'kg', 
    category: 'MEDIDAS DE FUERZA Y COMPOSICIÓN CORPORAL',
    icon: <FitnessCenterIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'grasa_corporal', 
    label: 'Grasa Corporal', 
    unit: '%', 
    inverse: true,
    category: 'MEDIDAS DE FUERZA Y COMPOSICIÓN CORPORAL',
    icon: <FitnessCenterIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'longitud_mano', 
    label: 'Long. Mano', 
    unit: 'cm', 
    category: 'MEDIDAS ANTROPOMÉTRICAS',
    icon: <PersonIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'ancho_mano', 
    label: 'Ancho Mano', 
    unit: 'cm', 
    category: 'MEDIDAS ANTROPOMÉTRICAS',
    icon: <PersonIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'press_banca', 
    label: 'Press Banca', 
    unit: 'kg', 
    category: 'MEDIDAS DE FUERZA Y COMPOSICIÓN CORPORAL',
    icon: <FitnessCenterIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'agilidad', 
    label: 'Agilidad', 
    unit: 's', 
    inverse: true,
    category: 'PRUEBAS DE AGILIDAD Y VELOCIDAD',
    icon: <TimerIcon sx={{ fontSize: 20 }} />
  },
  { 
    key: 'sprint_cuarto', 
    label: 'Sprint 3/4', 
    unit: 's', 
    inverse: true,
    category: 'PRUEBAS DE AGILIDAD Y VELOCIDAD',
    icon: <SpeedIcon sx={{ fontSize: 20 }} />
  }
];

const renderMedidaContent = (medida, previousMedida) => {
  // Afficher toutes les valeurs sur une ligne
  const allMetrics = metrics.filter(metric => medida[metric.key]);
  
  if (allMetrics.length === 0) return <Typography color="text.secondary">Sin medidas disponibles</Typography>;
  
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Grid container spacing={2} sx={{ minWidth: allMetrics.length > 6 ? '800px' : 'auto' }}>
        {allMetrics.map(metric => {
          const value = medida[metric.key];
          const previousValue = previousMedida?.[metric.key];
          const evolution = calculateEvolution(value, previousValue, metric.inverse);
          
          return (
            <Grid item key={metric.key} sx={{ minWidth: '120px' }}>
              <Box sx={{ textAlign: 'center', p: 1 }}>
                <Typography variant="caption" display="block" color="text.secondary">
                  {metric.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {value}{metric.unit && ` ${metric.unit}`}
                </Typography>
                {evolution && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
                    {getTrendIcon(evolution.trend)}
                    <Typography variant="caption" color={evolution.trend === 'up' ? 'success.main' : evolution.trend === 'down' ? 'error.main' : 'text.secondary'}>
                      {evolution.diff > 0 ? '+' : ''}{evolution.diff}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const AllMedidasTable = () => {
  const { t } = useTranslation();
  const { translateFromDB } = usePositions(t);
  const [medidas, setMedidas] = useState([]);
  const [filteredMedidas, setFilteredMedidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayerType, setSelectedPlayerType] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openRows, setOpenRows] = useState({});
  const [user, setUser] = useState(null);

  // États pour l'édition et la suppression
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentMedida, setCurrentMedida] = useState(null);
  const [editedMedida, setEditedMedida] = useState({});
  const [usValues, setUsValues] = useState({});
  const [medidaToDelete, setMedidaToDelete] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  useEffect(() => {
    // Récupérer l'utilisateur depuis localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchAllMedidas();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [medidas, searchTerm, selectedPlayerType]);

  const fetchAllMedidas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/medidas/all');
      
      let medidasData = response.data;
      
      // Filtrer selon le rôle de l'utilisateur
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        const userRole = userData.role?.toLowerCase();
        
        if (userRole === 'admin pro') {
          // Afficher uniquement les medidas des joueurs Pro
          medidasData = medidasData.filter(medida => 
            medida.player_type?.toLowerCase() === 'pro'
          );
        } else if (userRole === 'admin cantera') {
          // Afficher uniquement les medidas des joueurs Cantera
          medidasData = medidasData.filter(medida => 
            medida.player_type?.toLowerCase() === 'cantera'
          );
        }
        // Si ce n'est ni admin pro ni admin cantera, afficher toutes les medidas
      }
      
      setMedidas(medidasData);
      setFilteredMedidas(medidasData);
    } catch (error) {
      console.error("Error al recuperar las medidas:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = medidas;

    if (searchTerm) {
      filtered = filtered.filter(medida => {
        const searchLower = searchTerm.toLowerCase();
        return medida.player_nom.toLowerCase().includes(searchLower) ||
               medida.fuente.toLowerCase().includes(searchLower);
      });
    }

    if (selectedPlayerType && selectedPlayerType !== 'all') {
      // Sous PRO, on écarte le vivier « NCAA + » — même gate que l'affichage du bucket.
      // Hissé HORS du callback : sinon localStorage serait relu à chaque ligne.
      const excludeProspects = currentUserCanUseProspectFilter();
      filtered = filtered.filter(medida => matchesPlayerTypeValue(medida, selectedPlayerType, { excludeProspects }));
    }

    setFilteredMedidas(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeMobilePage = (event, newValue) => {
    setPage(newValue - 1);
  };

  const handleToggleRow = (rowId) => {
    setOpenRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const navigateToPlayer = (playerId) => {
    navigate(`/players/${playerId}`);
  };

  // Handlers pour l'édition
  const handleEditClick = (medida) => {
    setCurrentMedida(medida);
    const edited = {
      fecha: medida.fecha ? new Date(medida.fecha).toISOString().split('T')[0] : '',
      fuente: medida.fuente || '',
      altura: medida.altura || '',
      envergadura: medida.envergadura || '',
      alcance: medida.alcance || '',
      salto_vertical_maximo: medida.salto_vertical_maximo || '',
      salto_vertical_de_pie: medida.salto_vertical_de_pie || '',
      peso: medida.peso || '',
      grasa_corporal: medida.grasa_corporal || '',
      longitud_mano: medida.longitud_mano || '',
      ancho_mano: medida.ancho_mano || '',
      agilidad: medida.agilidad || '',
      sprint_cuarto: medida.sprint_cuarto || ''
    };
    setEditedMedida(edited);

    // Initialiser les valeurs US
    const us = {};
    if (edited.altura) {
      const { feet, inches } = cmToFeetInches(edited.altura);
      us.alturaFeet = feet.toString();
      us.alturaInches = inches;
    }
    if (edited.envergadura) {
      const { feet, inches } = cmToFeetInches(edited.envergadura);
      us.envergaduraFeet = feet.toString();
      us.envergaduraInches = inches;
    }
    if (edited.alcance) {
      const { feet, inches } = cmToFeetInches(edited.alcance);
      us.alcanceFeet = feet.toString();
      us.alcanceInches = inches;
    }
    if (edited.salto_vertical_maximo) {
      us.saltoVerticalMaximoInches = cmToInches(edited.salto_vertical_maximo);
    }
    if (edited.salto_vertical_de_pie) {
      us.saltoVerticalDePieInches = cmToInches(edited.salto_vertical_de_pie);
    }
    if (edited.peso) {
      us.pesoLbs = kgToLbs(edited.peso);
    }
    if (edited.longitud_mano) {
      us.longitudManoInches = cmToInches(edited.longitud_mano);
    }
    if (edited.ancho_mano) {
      us.anchoManoInches = cmToInches(edited.ancho_mano);
    }
    setUsValues(us);
    setOpenEditDialog(true);
  };

  const handleEditSave = async () => {
    try {
      const cleanedData = {
        ...editedMedida,
        altura: editedMedida.altura || null,
        envergadura: editedMedida.envergadura || null,
        alcance: editedMedida.alcance || null,
        salto_vertical_maximo: editedMedida.salto_vertical_maximo || null,
        salto_vertical_de_pie: editedMedida.salto_vertical_de_pie || null,
        peso: editedMedida.peso || null,
        grasa_corporal: editedMedida.grasa_corporal || null,
        longitud_mano: editedMedida.longitud_mano || null,
        ancho_mano: editedMedida.ancho_mano || null,
        agilidad: editedMedida.agilidad || null,
        sprint_cuarto: editedMedida.sprint_cuarto || null
      };

      await api.put(`/api/medidas/${currentMedida.id}`, cleanedData);
      fetchAllMedidas();
      setOpenEditDialog(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
    }
  };

  // Handlers pour la suppression
  const handleDeleteClick = (medida) => {
    setMedidaToDelete(medida);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/medidas/${medidaToDelete.id}`);
      fetchAllMedidas();
      setOpenDeleteDialog(false);
      setMedidaToDelete(null);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
    }
  };

  const totalResults = filteredMedidas.length;
  const paginatedData = React.useMemo(() => {
    if (rowsPerPage <= 0) {
      return filteredMedidas;
    }
    return filteredMedidas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredMedidas, page, rowsPerPage]);

  // Créer une map pour accéder rapidement aux médidas précédentes par joueur
  const previousMedidasMap = React.useMemo(() => {
    const map = {};
    
    // Grouper les medidas par joueur
    const medidasByPlayer = {};
    medidas.forEach(medida => {
      if (!medidasByPlayer[medida.player_id]) {
        medidasByPlayer[medida.player_id] = [];
      }
      medidasByPlayer[medida.player_id].push(medida);
    });
    
    // Pour chaque joueur, trier par date et créer la map
    Object.keys(medidasByPlayer).forEach(playerId => {
      const playerMedidas = medidasByPlayer[playerId].sort((a, b) => 
        new Date(b.fecha) - new Date(a.fecha)
      );
      
      playerMedidas.forEach((medida, index) => {
        if (index < playerMedidas.length - 1) {
          map[medida.id] = playerMedidas[index + 1];
        }
      });
    });
    
    return map;
  }, [medidas]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pb: isMobile ? 7 : 0 }}>
      {isMobile ? (
        <>
          {/* Mobile View */}
          <Box
            sx={{
              mx: 2,
              mt: 2,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.1rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="subtitle1">
              {totalResults === 1 ? t('allMedidas.measureFound') : t('allMedidas.measuresFound')}
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('allMedidas.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ mb: 2 }}
            />

            {user?.role?.toLowerCase() === 'super admin' && (
              <Box sx={{ mb: 2 }}>
                <PlayerTypeToggle
                  value={selectedPlayerType}
                  onChange={(val) => setSelectedPlayerType(val)}
                  t={t}
                  tPrefix="allMedidas"
                />
              </Box>
            )}

            {paginatedData.map((medida) => (
              <MobileMedidaCard
                key={medida.id}
                medida={medida}
                open={openRows[medida.id] || false}
                onToggle={handleToggleRow}
                navigate={navigate}
                previousMedida={previousMedidasMap[medida.id]}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                t={t}
                translateFromDB={translateFromDB}
              />
            ))}
          </Box>

          {/* Mobile Pagination */}
          {rowsPerPage !== -1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Pagination
                color="primary"
                count={Math.ceil(totalResults / rowsPerPage)}
                page={page + 1}
                onChange={handleChangeMobilePage}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          {/* Desktop View */}
          <Grid container spacing={2} marginBottom={2}>
            <Grid item xs={12} sm={user?.role?.toLowerCase() === 'super admin' ? 8 : 12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('allMedidas.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {user?.role?.toLowerCase() === 'super admin' && (
              <Grid item xs={12} sm={4}>
                <PlayerTypeToggle
                  value={selectedPlayerType}
                  onChange={(val) => setSelectedPlayerType(val)}
                  t={t}
                  tPrefix="allMedidas"
                />
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              borderRadius: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                fontWeight: 'bold',
                fontSize: '1.2rem',
              }}
            >
              {totalResults}
            </Box>
            <Typography variant="h6">
              {totalResults === 1 ? t('allMedidas.measureFound') : t('allMedidas.measuresFound')}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">{t('common.date')}</TableCell>
                  <TableCell>{t('allMedidas.player')}</TableCell>
                  <TableCell align="center">{t('allMedidas.position')}</TableCell>
                  <TableCell align="center">{t('allMedidas.age')}</TableCell>
                  <TableCell align="center">{t('allMedidas.nationality')}</TableCell>
                  <TableCell align="center">{t('allMedidas.team')}</TableCell>
                  <TableCell align="center">{t('playerCardCreator.medidasForm.event')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                  <TableCell align="center">{t('common.details')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row) => {
                  const birthYear = row.player_date_naissance ? new Date(row.player_date_naissance).getFullYear() : null;
                  const decimalAge = calculateAge(row.player_date_naissance, row.fecha);
                  const shortNat = getShortNationality(row.player_nationalite);
                  const previousMedida = previousMedidasMap[row.id];

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        onClick={(e) => {
                          // Ne pas toggler si on clique sur un lien, bouton ou action
                          if (
                            e.target.closest('button') ||
                            e.target.closest('[role="button"]') ||
                            e.target.closest('[data-player-link]')
                          ) {
                            return;
                          }
                          handleToggleRow(row.id);
                        }}
                      >
                        <TableCell align="center">
                          {new Date(row.fecha).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              src={getPlayerAvatarUrl(row.player_id)}
                              alt={row.player_nom}
                              sx={{ mr: 1, cursor: 'pointer' }}
                              data-player-link
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToPlayer(row.player_id);
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              data-player-link
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToPlayer(row.player_id);
                              }}
                            >
                              {row.player_nom}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">{translateFromDB(row.player_poste) || '-'}</TableCell>
                        <TableCell align="center">
                          {birthYear && decimalAge ? `${birthYear} (${decimalAge})` : '-'}
                        </TableCell>
                        <TableCell align="center">{shortNat || '-'}</TableCell>
                        <TableCell align="center">
                          <Box>
                            {/* Si on a des données dans competitions_detail, les afficher en priorité */}
                            {row.player_competitions_detail && row.player_competitions_detail.length > 0 ? (
                              <>
                                {/* Grouper par équipe */}
                                {[...new Set(row.player_competitions_detail.map(c => c.equipe_nom))].map((equipe, idx) => {
                                  // Récupérer l'ID de l'équipe et les compétitions associées
                                  const equipeData = row.player_competitions_detail.find(c => c.equipe_nom === equipe);
                                  const competitions = row.player_competitions_detail
                                    .filter(c => c.equipe_nom === equipe)
                                    .filter((value, index, self) =>
                                      index === self.findIndex((c) => c.competition_id === value.competition_id)
                                    );
                                  const mediaUrl = getMediaBaseUrl();
                                  const timestamp = Date.now();

                                  return (
                                    <Box key={idx} sx={{ mb: 0.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        {/* Logo de l'équipe */}
                                        <Box
                                          component="img"
                                          src={`${mediaUrl}/equipes/${equipeData.equipe_id}.jpg?t=${timestamp}`}
                                          alt={equipe}
                                          sx={{
                                            width: 16,
                                            height: 16,
                                            objectFit: 'contain',
                                            borderRadius: '2px'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{equipe}</div>
                                      </Box>
                                      {/* Logos et noms des compétitions */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, flexWrap: 'wrap', mt: 0.3 }}>
                                        {competitions.map((comp, compIdx) => (
                                          <Box key={compIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                            <Box
                                              component="img"
                                              src={`${mediaUrl}/competitions/${comp.competition_id}.jpg?t=${timestamp}`}
                                              alt={comp.competition_nom}
                                              sx={{
                                                width: 14,
                                                height: 14,
                                                objectFit: 'contain',
                                                borderRadius: '2px'
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                            <span style={{ fontSize: '10px' }}>{comp.competition_nom}</span>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </>
                            ) : (
                              // Sinon afficher l'ancien système (team et optionnellement les ligues)
                              <Box>
                                {row.player_team ? (
                                  <div style={{ fontSize: '12px' }}>{row.player_team}</div>
                                ) : (
                                  <div style={{ fontSize: '12px' }}>-</div>
                                )}
                                {(row.player_league_1 || row.player_league_2) && (
                                  <div style={{ fontSize: '10px', color: '#666' }}>
                                    {[row.player_league_1, row.player_league_2].filter(Boolean).join(' - ')}
                                  </div>
                                )}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{row.fuente}</TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title={t('common.edit')}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(row)}
                                sx={{ color: 'primary.main' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('common.delete')}>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(row)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleRow(row.id);
                            }}
                          >
                            {openRows[row.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Détails en Collapse */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                          <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                            <Box margin={2}>
                              {renderMedidaContent(row, previousMedida)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            {/* Desktop Pagination */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100, { label: t('common.all'), value: -1 }]}
              component="div"
              count={totalResults}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t('allMedidas.measuresPerPage')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('allMedidas.of')} ${count}`}
            />
          </TableContainer>
        </>
      )}

      {/* Dialog d'édition avec conversions US/EU */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: '92vh',
            maxHeight: '92vh'
          }
        }}
      >
        <DialogTitle sx={{ py: 1 }}>{t('allMedidas.editMeasure')}</DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          {currentMedida && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <TextField
                  label={t('playerCardCreator.medidasForm.date')}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editedMedida.fecha}
                  onChange={e => setEditedMedida(prev => ({ ...prev, fecha: e.target.value }))}
                  size="small"
                />
                <TextField
                  label={t('playerCardCreator.medidasForm.event')}
                  fullWidth
                  value={editedMedida.fuente}
                  onChange={e => setEditedMedida(prev => ({ ...prev, fuente: e.target.value }))}
                  size="small"
                />
              </Box>

              {/* Grille de toutes les mesures sans titres de sections */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                {/* Altura sin zapatos */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.heightWithoutShoes')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="6"
                      value={usValues.alturaFeet || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setUsValues(prev => ({ ...prev, alturaFeet: value }));
                        const cm = feetInchesToCm(value, usValues.alturaInches);
                        setEditedMedida(prev => ({ ...prev, altura: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">ft</Typography>
                    <TextField
                      size="small"
                      placeholder="2.5"
                      value={usValues.alturaInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, alturaInches: value }));
                        const cm = feetInchesToCm(usValues.alturaFeet, value);
                        setEditedMedida(prev => ({ ...prev, altura: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.altura}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, altura: value }));
                        if (value) {
                          const { feet, inches } = cmToFeetInches(value);
                          setUsValues(prev => ({ ...prev, alturaFeet: feet.toString(), alturaInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Envergadura */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.wingspan')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="7"
                      value={usValues.envergaduraFeet || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setUsValues(prev => ({ ...prev, envergaduraFeet: value }));
                        const cm = feetInchesToCm(value, usValues.envergaduraInches);
                        setEditedMedida(prev => ({ ...prev, envergadura: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">ft</Typography>
                    <TextField
                      size="small"
                      placeholder="2"
                      value={usValues.envergaduraInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, envergaduraInches: value }));
                        const cm = feetInchesToCm(usValues.envergaduraFeet, value);
                        setEditedMedida(prev => ({ ...prev, envergadura: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.envergadura}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, envergadura: value }));
                        if (value) {
                          const { feet, inches } = cmToFeetInches(value);
                          setUsValues(prev => ({ ...prev, envergaduraFeet: feet.toString(), envergaduraInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Alcance */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.standingReach')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="8"
                      value={usValues.alcanceFeet || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setUsValues(prev => ({ ...prev, alcanceFeet: value }));
                        const cm = feetInchesToCm(value, usValues.alcanceInches);
                        setEditedMedida(prev => ({ ...prev, alcance: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">ft</Typography>
                    <TextField
                      size="small"
                      placeholder="10"
                      value={usValues.alcanceInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, alcanceInches: value }));
                        const cm = feetInchesToCm(usValues.alcanceFeet, value);
                        setEditedMedida(prev => ({ ...prev, alcance: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.alcance}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, alcance: value }));
                        if (value) {
                          const { feet, inches } = cmToFeetInches(value);
                          setUsValues(prev => ({ ...prev, alcanceFeet: feet.toString(), alcanceInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Peso */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.weight')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="220"
                      value={usValues.pesoLbs || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, pesoLbs: value }));
                        const kg = lbsToKg(value);
                        setEditedMedida(prev => ({ ...prev, peso: kg.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">lbs</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.peso}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, peso: value }));
                        if (value) {
                          const lbs = kgToLbs(value);
                          setUsValues(prev => ({ ...prev, pesoLbs: lbs }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">kg</Typography>
                  </Box>
                </Box>

                {/* Longitud Mano */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.handLength')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="9"
                      value={usValues.longitudManoInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, longitudManoInches: value }));
                        const cm = inchesToCm(value);
                        setEditedMedida(prev => ({ ...prev, longitud_mano: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.longitud_mano}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, longitud_mano: value }));
                        if (value) {
                          const inches = cmToInches(value);
                          setUsValues(prev => ({ ...prev, longitudManoInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Ancho Mano */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.handWidth')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="8"
                      value={usValues.anchoManoInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, anchoManoInches: value }));
                        const cm = inchesToCm(value);
                        setEditedMedida(prev => ({ ...prev, ancho_mano: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.ancho_mano}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, ancho_mano: value }));
                        if (value) {
                          const inches = cmToInches(value);
                          setUsValues(prev => ({ ...prev, anchoManoInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Grasa Corporal */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.bodyFat')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="6.5"
                      value={editedMedida.grasa_corporal}
                      onChange={e => setEditedMedida(prev => ({ ...prev, grasa_corporal: e.target.value }))}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">%</Typography>
                  </Box>
                </Box>

                {/* Salto Vertical de Pie */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.standingVertical')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="32"
                      value={usValues.saltoVerticalDePieInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, saltoVerticalDePieInches: value }));
                        const cm = inchesToCm(value);
                        setEditedMedida(prev => ({ ...prev, salto_vertical_de_pie: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.salto_vertical_de_pie}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, salto_vertical_de_pie: value }));
                        if (value) {
                          const inches = cmToInches(value);
                          setUsValues(prev => ({ ...prev, saltoVerticalDePieInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Salto Vertical Máximo */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.maxVertical')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="38"
                      value={usValues.saltoVerticalMaximoInches || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setUsValues(prev => ({ ...prev, saltoVerticalMaximoInches: value }));
                        const cm = inchesToCm(value);
                        setEditedMedida(prev => ({ ...prev, salto_vertical_maximo: cm.toFixed(2) }));
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">in</Typography>
                    <Typography sx={{ mx: 0.5 }}>→</Typography>
                    <TextField
                      size="small"
                      value={editedMedida.salto_vertical_maximo}
                      onChange={e => {
                        const value = e.target.value;
                        setEditedMedida(prev => ({ ...prev, salto_vertical_maximo: value }));
                        if (value) {
                          const inches = cmToInches(value);
                          setUsValues(prev => ({ ...prev, saltoVerticalMaximoInches: inches }));
                        }
                      }}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">cm</Typography>
                  </Box>
                </Box>

                {/* Agilidad */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.agility')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="11.20"
                      value={editedMedida.agilidad}
                      onChange={e => setEditedMedida(prev => ({ ...prev, agilidad: e.target.value }))}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">s</Typography>
                  </Box>
                </Box>

                {/* Sprint 3/4 */}
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                    {t('playerCardCreator.medidasForm.sprint')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="3.10"
                      value={editedMedida.sprint_cuarto}
                      onChange={e => setEditedMedida(prev => ({ ...prev, sprint_cuarto: e.target.value }))}
                      sx={{ width: 80 }}
                    />
                    <Typography variant="caption">s</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            {t('allMedidas.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de suppression */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('allMedidas.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('allMedidas.confirmDeleteMessage', { date: medidaToDelete?.fecha ? new Date(medidaToDelete.fecha).toLocaleDateString() : '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllMedidasTable;